import { useAuthStore } from '@/stores/authStore'
import { hasPermission, hasAnyPermission } from '@/lib/permissions'

interface PermissionGateProps {
  /** Single permission to check */
  permission?: string
  /** Multiple permissions -- shows content if user has ANY of them */
  anyOf?: string[]
  /** Multiple permissions -- shows content only if user has ALL of them */
  allOf?: string[]
  /** Content to show when permission is granted */
  children: React.ReactNode
  /** Optional fallback to show when permission is denied */
  fallback?: React.ReactNode
}

/**
 * Conditionally renders children based on the current user's permissions.
 *
 * Usage:
 *   <PermissionGate permission="vendors:activate">
 *     <button>Activate</button>
 *   </PermissionGate>
 *
 *   <PermissionGate anyOf={['vendors:activate', 'vendors:suspend']}>
 *     <ActionMenu />
 *   </PermissionGate>
 */
export default function PermissionGate({
  permission,
  anyOf,
  allOf,
  children,
  fallback = null,
}: PermissionGateProps) {
  const { user } = useAuthStore()
  const role = user?.role

  if (!role) return <>{fallback}</>

  let allowed = false

  if (permission) {
    allowed = hasPermission(role, permission)
  } else if (anyOf) {
    allowed = hasAnyPermission(role, anyOf)
  } else if (allOf) {
    allowed = allOf.every((p) => hasPermission(role, p))
  } else {
    allowed = true
  }

  return <>{allowed ? children : fallback}</>
}
